import * as React from 'react'
import { useHistory } from 'react-router'
import { useObservableCallback } from 'hooks/rx-hooks'
import { OutlineViewModel } from '../OutlineViewModel'

/**
 * Syncs the URL hash with the active step.
 *
 * @param outline
 */
export function useSyncStepWithHash(outline: OutlineViewModel) {
  const [hash, setHash] = React.useState(window.location.hash)
  const isFirstRunRef = React.useRef(true)
  const history = useHistory()
  React.useEffect(() => {
    const handler = () => setHash(window.location.hash)
    window.addEventListener('hashchange', handler)
    return () => {
      window.removeEventListener('hashchange', handler)
    }
  })
  React.useEffect(() => {
    if (hash) {
      const id = hash.substring(1)
      // The `hash` starts with `#`, so cut that off.
      outline.requestStepWithIdBecomeActive(id)
      // Ensures the hash matches in case nothing was changed.
      if (outline.activeStep) {
        replaceHash(outline.activeStep.stepId.toLowerCase())
      }
    } else {
      outline.requestPendingStepBecomeActive()
    }
  }, [outline, hash])

  useObservableCallback(
    outline.activeStep$,
    (step) => {
      if (step) {
        const id = step.stepId.toLowerCase()
        // This avoids an issue where if you hit the back button it just clears
        // the hash. This is because assigning the hash like we do in the `else`
        // branch will push history.
        if (isFirstRunRef.current && !window.location.hash) {
          replaceHash(id)
        } else {
          window.location.hash = id
        }
        isFirstRunRef.current = false
      }
    },
    [outline, history],
  )
}

function replaceHash(id: string) {
  window.location.replace(`#${id}`)
}
